import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, ClickAwayListener, Paper } from '@mui/material';
import clsx from 'clsx';
import { useCheckIsDocShared } from 'hooks';
import { isEmpty } from 'lodash';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan, getBusinessToolTitle } from 'store/useBusinessToolStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { PdfDownloadButton } from 'pages/BusinessToolPage/BusinessToolHeader/PdfDownloadButton';
import { useAIActionHandler } from 'pages/BusinessToolPage/components/BusinessToolAiProvider/hooks/useAIActionHandler';

import { ReactComponent as ArrowPathIcon } from 'assets/icons/ArrowPathIcon.svg';
import { ReactComponent as ArrowRightUpIcon } from 'assets/icons/ArrowRightUpIcon.svg';
import { ReactComponent as ChainsIcon } from 'assets/icons/ChainsIcon.svg';
import { ReactComponent as PaperAirplaneIcon } from 'assets/icons/PaperAirplaneIcon.svg';

import { useAuthenticated } from 'hooks/useAuthenticated';
import { useCurrentSubscription } from 'hooks/useCurrentSubscription';
import { useManageDocumentShare } from 'hooks/useManageDocumentShare';
import usePromiseStatus from 'hooks/usePromiseStatus';
import { useSharePDF } from 'hooks/useSharePDF';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { viewDocHandler } from 'helpers/viewDocHandler';

import { MenuList } from './components/MenuList';
import { ShareButton } from './components/ShareButton';
import { IShareMenuItem } from './types';

import styles from './ShareDocumentButton.module.scss';

export const ShareDocumentButton: FC = () => {
  const { t } = useTranslation();

  const pdfGenerationRef = useRef<HTMLButtonElement>(null);

  const [isShareMenuOpened, setIsShareMenuOpened] = useState(false);
  const [shouldDocUpdate, setShouldDocUpdate] = useState(false);

  const { handleActionWithAICheck } = useAIActionHandler();
  const isDocumentBeingShared = useCheckIsDocShared();

  const { track, isPending } = usePromiseStatus();

  const businessPlanTitle = useBusinessToolStore(getBusinessToolTitle);
  const businessPlan = useBusinessToolStore(getBusinessPlan);

  const currentSubscription = useCurrentSubscription();

  const { isAuthenticated } = useAuthenticated();

  const isPDFDocCreated = !isEmpty(businessPlan.metadata.pdfShareLink);

  useEffect(() => {
    const previousPdfShareLink = localStorage.getItem(`businessPlanPreviousUpdatedInfo:${businessPlan.uuid}`)
      ? JSON.parse(localStorage.getItem(`businessPlanPreviousUpdatedInfo:${businessPlan.uuid}`)!).pdfShareLink
      : '';
    const previousUpdatedAt = localStorage.getItem(`businessPlanPreviousUpdatedInfo:${businessPlan.uuid}`)
      ? JSON.parse(localStorage.getItem(`businessPlanPreviousUpdatedInfo:${businessPlan.uuid}`)!).updatedAt
      : '';
    if (
      localStorage.getItem(`businessPlanPreviousUpdatedInfo:${businessPlan.uuid}`) &&
      previousPdfShareLink === businessPlan.metadata.pdfShareLink &&
      previousUpdatedAt !== businessPlan.updatedAt
    ) {
      setShouldDocUpdate(true);
    } else if (businessPlan.uuid) {
      const businessPlanPreviousUpdatedInfo = {
        updatedAt: businessPlan.updatedAt,
        pdfShareLink: businessPlan.metadata.pdfShareLink,
      };
      localStorage.setItem(
        `businessPlanPreviousUpdatedInfo:${businessPlan.uuid}`,
        JSON.stringify(businessPlanPreviousUpdatedInfo),
      );
      setShouldDocUpdate(false);
    }
  }, [businessPlan.updatedAt]);

  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;
  const isPDFGenerationAvailable = businessPlan.metadata.updateOnlinePDFCount < 2 || isStandardOrLightTariff; // here we check for 2, because we include here first online pdf creation

  const pdfGenerationHandler = () => {
    if (!isPDFGenerationAvailable) {
      return;
    }
    setShouldDocUpdate(false);
    const businessPlanPreviousUpdatedInfo = {
      updatedAt: businessPlan.updatedAt,
      pdfShareLink: businessPlan.metadata.pdfShareLink,
    };
    localStorage.setItem(
      `businessPlanPreviousUpdatedInfo:${businessPlan.uuid}`,
      JSON.stringify(businessPlanPreviousUpdatedInfo),
    );
    pdfGenerationRef.current?.click();
  };

  const viewDocument = () => {
    if (!isPDFGenerationAvailable) {
      return;
    }
    viewDocHandler(businessPlan.uuid);
  };

  const { copyLinkHandler, handleFileUpload, inviteAssistantsModal } = useSharePDF({
    uuid: businessPlan.uuid,
    pdfGenerationHandler,
    track,
    metadata: businessPlan.metadata,
    docTitle: businessPlanTitle,
    status: businessPlan.status,
  });

  const { sharePDFLinkModal } = useManageDocumentShare({ uuid: businessPlan.uuid });

  const inviteAssistantItem: IShareMenuItem = {
    title: t('inviteAssistant'),
    icon: <PaperAirplaneIcon />,
    disabled: isDocumentBeingShared,
    clickHandler: inviteAssistantsModal,
  };

  const shareMenuContent: IShareMenuItem[] = [
    { title: t('createOnlinePDF'), icon: <ArrowRightUpIcon />, clickHandler: pdfGenerationHandler },
    inviteAssistantItem,
  ];

  const createdPDFMenuContent: IShareMenuItem[] = [
    {
      title: t('copyLink'),
      icon: <ChainsIcon />,
      clickHandler: copyLinkHandler,
      buttonTitle: t('manage'),
      buttonClickHandler: sharePDFLinkModal,
    },
    {
      title: t('updateOnlinePDF'),
      icon: <ArrowPathIcon />,
      clickHandler: pdfGenerationHandler,
      buttonTitle: t('view'),
      buttonClickHandler: viewDocument,
      isPDFUpdated: true,
      disabled: !shouldDocUpdate,
    },
    inviteAssistantItem,
  ];

  const displayItems = isPDFDocCreated ? createdPDFMenuContent : shareMenuContent;

  const openMenuHandler = () =>
    handleActionWithAICheck(() => {
      if (!isAuthenticated || !isStandardOrLightTariff) {
        return;
      }
      setIsShareMenuOpened(true);
    });

  const clickAwayHandler = () => {
    setIsShareMenuOpened(false);
  };

  return (
    <Box className={styles.shareMenuWrapper}>
      <PdfDownloadButton ref={pdfGenerationRef} onFileUpload={handleFileUpload} className="sr-only" triggerFromShare />

      <Box>
        <ShareButton
          onClick={openMenuHandler}
          isDisabled={isPending}
          shouldDocUpdate={shouldDocUpdate}
          isDocCreated={isPDFDocCreated}
        />
      </Box>

      {isShareMenuOpened && (
        <ClickAwayListener onClickAway={clickAwayHandler}>
          <Paper className={clsx(styles.wrapper, styles.menuWrapper)} elevation={2}>
            <MenuList
              clickAwayHandler={clickAwayHandler}
              displayItems={displayItems}
              isDocCreated={isPDFDocCreated}
              shouldDocUpdate={shouldDocUpdate}
            />
          </Paper>
        </ClickAwayListener>
      )}
    </Box>
  );
};
